import {
  EphemeralNotificationOutcome,
  EphemeralNotificationRecord,
  EphemeralNotificationsPersonalSettings,
} from '@ardoq/api-types';
import {
  addHours,
  differenceInDays,
  differenceInMinutes,
  parseDate,
} from '@ardoq/date-time';
import { EphemeralNotificationKeys, EphemeralResolveDelay } from './types';

export type EphemeralNotificationRecords =
  EphemeralNotificationsPersonalSettings['records'];

export const VIEW_DWELL_MS = 1000;

const SESSION_GAP_MINUTES = 90;

const KNOWN_OUTCOME_STATUSES: ReadonlyArray<
  EphemeralNotificationOutcome['status']
> = ['seen', 'dismissed', 'completed'];

const UNKNOWN_STATUS_FALLBACK: EphemeralNotificationOutcome = {
  status: 'dismissed',
};

/** Falls back for an unrecognised or missing status — `outcome` comes from an unvalidated personal setting. */
const toKnownOutcome = (
  outcome: EphemeralNotificationOutcome | undefined
): EphemeralNotificationOutcome =>
  outcome && KNOWN_OUTCOME_STATUSES.includes(outcome.status)
    ? outcome
    : UNKNOWN_STATUS_FALLBACK;

const isOutcomeResolved = (
  outcome: EphemeralNotificationOutcome | undefined
): boolean => {
  switch (toKnownOutcome(outcome).status) {
    case 'seen':
      return false;
    case 'dismissed':
    case 'completed':
      return true;
  }
};

const isNotificationResolved = (
  records: EphemeralNotificationRecords,
  notificationKey: EphemeralNotificationKeys
): boolean => {
  const record = records[notificationKey];
  return record ? isOutcomeResolved(record.outcome) : false;
};

type IsWithinPersonalWindowArgs = {
  record: EphemeralNotificationRecord | undefined;
  personalWindowDays: number;
  now: Date;
};

const isWithinPersonalWindow = ({
  record,
  personalWindowDays,
  now,
}: IsWithinPersonalWindowArgs): boolean => {
  if (!record) return true;
  const daysSinceFirstSeen = differenceInDays(
    now,
    parseDate(record.firstSeenAt)
  );
  return (
    !Number.isFinite(daysSinceFirstSeen) ||
    daysSinceFirstSeen < 0 ||
    daysSinceFirstSeen <= personalWindowDays
  );
};

type IsWithinViewCapArgs = {
  record: EphemeralNotificationRecord | undefined;
  maxViews: number;
};

const isWithinViewCap = ({ record, maxViews }: IsWithinViewCapArgs): boolean =>
  !record || (record.viewCount ?? 0) < maxViews;

type IsResolveDelayElapsedArgs = {
  resolvableAfter: EphemeralResolveDelay | undefined;
  record: EphemeralNotificationRecord | undefined;
  now: Date;
};

/** A corrupted `firstSeenAt` elapses too, rather than blocking resolution forever. */
const isResolveDelayElapsed = ({
  resolvableAfter,
  record,
  now,
}: IsResolveDelayElapsedArgs): boolean => {
  if (!resolvableAfter) return true;
  if (!record) return false;
  if ('views' in resolvableAfter) {
    return (record.viewCount ?? 0) >= resolvableAfter.views;
  }
  const resolvesAt = addHours(
    parseDate(record.firstSeenAt),
    resolvableAfter.hours
  );
  return !Number.isFinite(resolvesAt.valueOf()) || now >= resolvesAt;
};

const shouldCountView = (
  record: EphemeralNotificationRecord | undefined,
  now: Date
): boolean => {
  if (!record?.lastCountedViewAt) return true;
  const minutesSinceLastView = differenceInMinutes(
    now,
    parseDate(record.lastCountedViewAt)
  );
  return (
    !Number.isFinite(minutesSinceLastView) ||
    minutesSinceLastView < 0 ||
    minutesSinceLastView >= SESSION_GAP_MINUTES
  );
};

const hasNoCountedView = (
  record: EphemeralNotificationRecord | undefined
): boolean => (record?.viewCount ?? 0) === 0;

const withCountedView = (
  record: EphemeralNotificationRecord | undefined,
  now: Date
): EphemeralNotificationRecord => {
  const nowIso = now.toISOString();
  if (!record) {
    return {
      firstSeenAt: nowIso,
      lastCountedViewAt: nowIso,
      viewCount: 1,
      outcome: { status: 'seen' },
    };
  }
  return {
    ...record,
    lastCountedViewAt: nowIso,
    viewCount: (record.viewCount ?? 0) + 1,
  };
};

const KNOWN_NOTIFICATION_KEYS = new Set<string>(
  Object.values(EphemeralNotificationKeys)
);

/** Drops records for notifications that no longer exist. */
const pruneRecords = (
  records: EphemeralNotificationRecords
): EphemeralNotificationRecords =>
  Object.fromEntries(
    Object.entries(records).filter(([notificationKey]) =>
      KNOWN_NOTIFICATION_KEYS.has(notificationKey)
    )
  );

type RecordViewArgs = {
  records: EphemeralNotificationRecords;
  notificationKey: EphemeralNotificationKeys;
  now: Date;
};

const recordView = ({
  records,
  notificationKey,
  now,
}: RecordViewArgs): EphemeralNotificationRecords => {
  const prunedRecords = pruneRecords(records);
  const record = prunedRecords[notificationKey];
  if (!shouldCountView(record, now)) return prunedRecords;
  return {
    ...prunedRecords,
    [notificationKey]: withCountedView(record, now),
  };
};

type RecordOutcomeArgs = {
  records: EphemeralNotificationRecords;
  notificationKey: EphemeralNotificationKeys;
  outcome: EphemeralNotificationOutcome;
  now: Date;
};

/** A resolved outcome can't be overwritten — e.g. a stray Escape after a completing action must not downgrade it to `dismissed`. */
const recordOutcome = ({
  records,
  notificationKey,
  outcome,
  now,
}: RecordOutcomeArgs): EphemeralNotificationRecords => {
  const nowIso = now.toISOString();
  const existingRecord = records[notificationKey];
  const existingOutcome = existingRecord?.outcome;
  return {
    ...pruneRecords(records),
    [notificationKey]: {
      firstSeenAt: existingRecord?.firstSeenAt ?? nowIso,
      lastCountedViewAt: existingRecord?.lastCountedViewAt,
      viewCount: existingRecord?.viewCount ?? 0,
      outcome:
        existingOutcome && isOutcomeResolved(existingOutcome)
          ? toKnownOutcome(existingOutcome)
          : outcome,
    },
  };
};

/** `firstSeenAt` is synthetic: the legacy ledger this backfills from never stored it. */
const withLegacyDismissal = (
  records: EphemeralNotificationRecords,
  notificationKey: string,
  now: Date
): EphemeralNotificationRecords => {
  if (records[notificationKey]) return records;
  return {
    ...records,
    [notificationKey]: {
      firstSeenAt: now.toISOString(),
      viewCount: 0,
      outcome: { status: 'dismissed' },
    },
  };
};

export const ephemeralNotificationsStateOperations = {
  isOutcomeResolved,
  isNotificationResolved,
  isWithinPersonalWindow,
  isWithinViewCap,
  isResolveDelayElapsed,
  shouldCountView,
  hasNoCountedView,
  pruneRecords,
  recordView,
  recordOutcome,
  withLegacyDismissal,
};
